import { changeDate, toggleLoadingLocal } from "./dom.js";
import { refreshAssignments } from "./refresh.js";
import { showSuccessMessage, showErrorMessage } from "./messages.js";

export function registerAssignmentEvents() {
    document.addEventListener("change", handleChangeEvents);
    document.addEventListener("keydown", handleKeydownEvents);
    document.addEventListener("blur", handleBlurEvents, true);
    document.addEventListener("click", handleDateNavigation);
    document.addEventListener("click", handleDeleteClicks);
}

function handleKeydownEvents(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (
        target.classList.contains("update-field") &&
        target.dataset.field === "time"
    ) {
        target.dataset.manualEdit = "1";
    }
}

function handleBlurEvents(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (
        target.classList.contains("update-field") &&
        target.dataset.field === "time" &&
        target.dataset.pendingManualSave === "1"
    ) {
        const row = target.closest("tr");
        if (!row) return;

        target.dataset.pendingManualSave = "0";
        target.dataset.manualEdit = "0";
        saveAjax(row.dataset.id, target.dataset.field, target.value);
    }
}

function handleChangeEvents(event) {
    if (event.target.classList.contains("status-toggle")) {
        handleStatusToggle(event.target);
    } else if (event.target.classList.contains("update-field")) {
        handleFieldUpdate(event.target);
    }
}

function handleStatusToggle(checkbox) {
    const row = checkbox.closest("tr");
    if (!row) return;

    if (checkbox.checked) {
        row.querySelectorAll(".status-toggle").forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
        });
        applyStatusRowColor(row, checkbox.dataset.status);
        saveAjax(row.dataset.id, "status", checkbox.dataset.status);
    } else {
        checkbox.checked = true;
    }
}

function applyStatusRowColor(row, status) {
    const statusClasses = [
        "status-row-pending",
        "status-row-dispatched",
        "status-row-in-transit",
        "status-row-completed",
        "status-row-cancelled",
        "late-row",
    ];

    row.classList.remove(...statusClasses);

    const statusClassByValue = {
        pending: "status-row-pending",
        dispatched: "status-row-dispatched",
        in_transit: "status-row-in-transit",
        completed: "status-row-completed",
        cancelled: "status-row-cancelled",
    };

    const statusClass = statusClassByValue[status];
    if (statusClass) {
        row.classList.add(statusClass);
    }
}

function handleFieldUpdate(input) {
    const row = input.closest("tr");
    if (!row) return;

    if (
        input instanceof HTMLInputElement &&
        input.dataset.field === "time" &&
        input.dataset.manualEdit === "1"
    ) {
        // Keyboard edits in <input type="time"> can fire change before the user finishes.
        // Defer save until blur to avoid interrupting typing.
        input.dataset.pendingManualSave = "1";
        return;
    }

    if (input.dataset.field === "time") {
        input.dataset.manualEdit = "0";
        input.dataset.pendingManualSave = "0";
    }

    saveAjax(row.dataset.id, input.dataset.field, input.value);
}

function handleDateNavigation(event) {
    if (event.target.closest("#prevDay")) changeDate(-1);
    else if (event.target.closest("#nextDay")) changeDate(1);
}

function handleDeleteClicks(event) {
    const btn = event.target.closest(".delete-assignment");
    if (!btn) return;

    const row = btn.closest("tr");
    const id = row.dataset.id;
    const recurrenceId = row.dataset.recurrenceId;
    const translations = window.dashboardTranslations || {};

    if (recurrenceId) {
        showConfirmationModal({
            title:
                translations.delete_recurrence_title ||
                "Supprimer la récurrence ?",
            message:
                translations.delete_recurrence_message ||
                "Voulez-vous supprimer toute la série ou seulement cet élément ?",
            yesText: translations.delete_all || "Supprimer tout",
            noText: translations.delete_single || "Seulement celui-ci",
            swapButtons: true,
            yesClass: "bg-gray-400 hover:bg-gray-500",
            noClass: "bg-blue-600 hover:bg-blue-700",
            onYes: () => performDelete(id, true),
            onNo: () => performDelete(id, false),
        });
    } else {
        showConfirmationModal({
            title: translations.confirm_delete || "Confirmer la suppression",
            message:
                translations.delete_single_confirm ||
                "Êtes-vous sûr de vouloir supprimer cet élément ?",
            onYes: () => performDelete(id, false),
            onNo: () => {},
        });
    }
}

function saveAjax(id, field, value) {
    const row = document.querySelector(`tr[data-id="${id}"]`);
    const recurrenceId = row?.dataset.recurrenceId;

    if (field === "status") {
        performUpdate(id, field, value, false);
        return;
    }

    if (recurrenceId && recurrenceId !== "") {
        showRecurrenceModal(id, field, value);
    } else {
        performUpdate(id, field, value, false);
    }
}

function showRecurrenceModal(id, field, value) {
    const translations = window.dashboardTranslations || {};

    showConfirmationModal({
        title:
            translations.modify_all_recurrence ||
            "Modifier toute la récurrence ?",
        message:
            translations.recurrence_description ||
            "Voulez-vous appliquer ce changement à toutes les occurrences futures ?",
        yesText: translations.update_all || "Oui, tout modifier",
        noText: translations.update_single || "Non, seulement celle-ci",
        swapButtons: true,
        yesClass: "bg-gray-400 hover:bg-gray-500",
        noClass: "bg-blue-600 hover:bg-blue-700",
        onYes: () => performUpdate(id, field, value, true),
        onNo: () => performUpdate(id, field, value, false),
    });
}

function performUpdate(id, field, value, updateAll) {
    toggleLoadingLocal(true);

    const payload = { [field]: value };
    if (updateAll) {
        payload.update_all_recurrence = true;
    }

    fetch(`/deliveries/${id}/update`, {
        method: "POST",
        keepalive: true,
        headers: {
            "Content-Type": "application/json",
            "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')
                .content,
            "X-Socket-ID": window.Echo?.socketId?.() ?? "",
        },
        body: JSON.stringify(payload),
    })
        .then((res) => (res.ok ? res.json() : Promise.reject("Error")))
        .then((data) => {
            console.log("Update success", data);
            if (data.count && data.count > 1) {
                const translations = window.dashboardTranslations || {};
                showSuccessMessage(
                    `${data.count} ${translations.recurrence_assignments_updated || "assignations mis à jour avec succès"}`,
                );
                setTimeout(() => location.reload(), 2000);
            } else {
                showSuccessMessage(
                    window.dashboardTranslations?.updated ||
                        "Mis à jour avec succès",
                );
                // Status changes are already applied optimistically in the DOM.
                // For other fields, do a non-forced refresh (uses ?since= fast-path).
                if (field !== "status") {
                    refreshAssignments();
                }
            }
        })
        .catch((err) => {
            console.error("Update failed", err);
            showErrorMessage("Erreur lors de la sauvegarde.");
        })
        .finally(() => toggleLoadingLocal(false));
}

function performDelete(id, deleteAll) {
    const translations = window.dashboardTranslations || {};

    fetch(`/deliveries/${id}`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')
                .content,
        },
        body: JSON.stringify({ delete_all_recurrence: deleteAll }),
    })
        .then((res) => {
            if (!res.ok) {
                return res
                    .text()
                    .then((t) =>
                        Promise.reject({ status: res.status, body: t }),
                    );
            }
            return res.json();
        })
        .then((data) => {
            if (data.count && data.count > 1) {
                showSuccessMessage(
                    `${data.count} ${translations.delete_success_multiple || "éléments supprimés"}`,
                );
                setTimeout(() => location.reload(), 1000);
            } else {
                showSuccessMessage(
                    translations.delete_success_single || "Élément supprimé",
                );
                const row = document.querySelector(`tr[data-id="${id}"]`);
                if (row) row.remove();
                refreshAssignments({ force: true });
            }
        })
        .catch((err) => {
            if (err && err.status === 419) {
                showErrorMessage(
                    "Session expirée. Veuillez recharger la page.",
                );
            } else if (err && err.status === 403) {
                showErrorMessage("Accès non autorisé.");
            } else if (err && err.status === 404) {
                showErrorMessage("Assignation introuvable.");
            } else {
                showErrorMessage("Erreur lors de la suppression.");
            }
            console.error("Delete error", err);
        });
}
